import { authedFetch, errorFromResponse, errorFromSse } from './api';

/**
 * Streams one chat turn from a Cloud Function over Server-Sent Events.
 *
 * Resolves with the final payload ({ text, sources, timing, usage, model,
 * tier }) once the server sends `done`. Throws the server's sanitized error
 * when it sends `error`, and an AbortError when `signal` is aborted — which
 * is how a conversation that has been left (new chat, switched chat, closed)
 * stops streaming into whatever replaced it.
 *
 * onEvent receives every parsed payload as it arrives:
 *   metadata · safety · status · thinking · sources · chunk · reset · done
 *
 * `reset` means the chunks so far were a draft the model replaced after
 * searching the archive: the answer starts over from its next chunk, and the
 * resolved text is only what came after it.
 */
export async function streamChat(functionName, body, { signal, onEvent } = {}) {
  const response = await authedFetch(functionName, body, { signal });
  if (!response.ok) throw await errorFromResponse(response);
  if (!response.body) throw new Error('Network response was not ok');

  const reader = response.body.getReader();
  const decoder = new TextDecoder('utf-8');
  const onAbort = () => { reader.cancel().catch(() => {}); };
  if (signal) signal.addEventListener('abort', onAbort);

  let buffer = '';
  let text = '';
  let result = null;

  try {
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const parts = buffer.split('\n\n');
      buffer = parts.pop();
      for (const part of parts) {
        const line = part.split('\n').find((l) => l.startsWith('data: '));
        if (!line) continue;
        let data;
        try {
          data = JSON.parse(line.slice(6));
        } catch (err) {
          continue;
        }
        if (data.error) throw errorFromSse(data);
        if (data.reset) text = '';
        if (data.chunk) text += data.chunk;
        if (data.done) result = { ...data, text };
        if (onEvent) onEvent(data);
      }
    }
  } finally {
    if (signal) signal.removeEventListener('abort', onAbort);
  }

  if (signal && signal.aborted) {
    const err = new Error('aborted');
    err.name = 'AbortError';
    throw err;
  }
  if (!result) {
    if (!text) throw new Error('No reply was received. Please try again.');
    result = { done: true, text };
  }
  return result;
}
